import dayjs from "dayjs";
import type {
	AdminAnnouncementListItemDto,
	AnnouncementsControllerGetListDisplayStatus,
} from "@/app2/shared/data/gen";

export type { AnnouncementsControllerGetListDisplayStatus } from "@/app2/shared/data/gen";

/**
 * Admin list / filters: US date + 12-hour clock with AM/PM (`hh:mm`, not 24h `HH`).
 * Example: `02/20/2026 10:42 AM`.
 */
export const ANNOUNCEMENT_DELIVERY_DATETIME_FORMAT = "MM/DD/YYYY hh:mm A";

/** Date-only (same US date order as the delivery filter, without time). */
export const ANNOUNCEMENT_DELIVERY_DATE_ONLY_FORMAT = "MM/DD/YYYY";

/** Same shape as list/detail DTO fields used for status (see {@link AdminAnnouncementListItemDto}). */
export type AnnouncementAdminStatusInput = Pick<
	AdminAnnouncementListItemDto,
	"publishedAt" | "scheduledDeliveryDate" | "delivered"
>;

/**
 * Admin UI status for an announcement row/detail.
 * Publish-immediate announcements have null scheduledDeliveryDate; treat as past window (not "inactive").
 */
export function getAnnouncementAdminDisplayStatus(
	row: AnnouncementAdminStatusInput
): AnnouncementsControllerGetListDisplayStatus {
	const published = !!row.publishedAt;
	const delivered = !!row.delivered;
	const scheduled = row.scheduledDeliveryDate
		? new Date(row.scheduledDeliveryDate)
		: null;
	const now = new Date();

	if (!published && delivered) {
		return "inactive";
	}
	if (!published) {
		return "draft";
	}
	if (scheduled !== null && scheduled.getTime() > now.getTime()) {
		return "scheduled";
	}
	if (!delivered) {
		return "processing";
	}
	return "live";
}

/**
 * Sort/display date for “when” the announcement delivers: scheduled first, else publish time (immediate).
 * Used by the admin list column valueGetter; keep in sync with {@link formatAnnouncementDeliveryDateTime}.
 */
export function getAnnouncementEffectiveDeliveryDate(
	row: AnnouncementAdminStatusInput
): Date | null {
	if (row.scheduledDeliveryDate) {
		return new Date(row.scheduledDeliveryDate);
	}
	if (row.publishedAt) {
		return new Date(row.publishedAt);
	}
	return null;
}

/** Formats a single instant for the Delivery Date & Time column and filter (local timezone). */
export function formatDeliveryDateTimeDisplay(d: Date | null | undefined): string {
	if (!d || Number.isNaN(d.getTime())) {
		return "";
	}
	return dayjs(d).format(ANNOUNCEMENT_DELIVERY_DATETIME_FORMAT);
}

/**
 * Normalizes API strings or legacy Date values for delivery-date helpers.
 */
function toAnnouncementIsoString(value: unknown): string | null {
	if (value == null || value === "") {
		return null;
	}
	if (typeof value === "string") {
		return value;
	}
	if (value instanceof Date) {
		const t = value.getTime();
		return Number.isNaN(t) ? null : value.toISOString();
	}
	return null;
}

/**
 * Instant for author line / relative time: scheduled delivery when set, else publish time.
 * Accepts ISO strings or Date (legacy Strapi shapes). Falls back to “now” only if nothing is set.
 */
export function getAnnouncementAuthorDisplayDate(row: {
	publishedAt?: unknown;
	scheduledDeliveryDate?: unknown;
}): Date {
	const publishedAt = toAnnouncementIsoString(row.publishedAt);
	const scheduledDeliveryDate = toAnnouncementIsoString(row.scheduledDeliveryDate);
	const d = getAnnouncementEffectiveDeliveryDate({
		publishedAt,
		scheduledDeliveryDate,
		delivered: false,
	});
	if (d) {
		return d;
	}
	if (publishedAt) {
		return new Date(publishedAt);
	}
	return new Date();
}

/** Delivery column / sidebar: scheduled time when set, else publish time; includes draft + scheduled. */
export function formatAnnouncementDeliveryDateTime(row: AnnouncementAdminStatusInput): string {
	const d = getAnnouncementEffectiveDeliveryDate(row);
	return d ? formatDeliveryDateTimeDisplay(d) : "";
}
